import React, { useMemo } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import PainelTabela from "../../components/PainelTabela";
import { PageLoader } from "../../components/LoadingMarcenaria";
import { atribuicaoDe } from "../../components/ProjetistaExecutivo/regras";
import { useDados } from "../../context/DadosContext";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { fmtDiasEmFila } from "../../utils/calculos";
import { entradaNaAprovacao, estaEmAprovacao } from "../../utils/fluxoExecutivo";
import { F } from "../../utils/gerencialFields";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";

const COLUNAS = [
  { key: "cliente", label: "Cliente", noWrap: true },
  { key: "ambiente", label: "Ambiente" },
  // Quem fez o executivo (backend, somente leitura aqui).
  { key: "projetista", label: "Projetista", labelCurto: "projetista", width: "150px" },
  { key: "entregaContrato", label: "Entrega Prevista", labelCurto: "entrega", tipo: "data", width: "110px" },
  { key: "aprovacaoLimite", label: "Aprovação Limite", labelCurto: "limite", tipo: "data", width: "110px" },
  // Bloco TBL.5: gate RBAC preco:view
  { key: "valor", label: "Valor", labelCurto: "valor", tipo: "moeda", width: "110px", requerPermissao: "preco:view" },
  { key: "diasEmFila", label: "Dias em fila", labelCurto: "fila", width: "90px" },
  { key: "status", label: "Status", width: "100px" },
];

export default function AprovacaoExecutivo() {
  const { dados, erro, initialLoading } = useDados();
  const { atribuicoes } = useProjetistasExecutivo();

  // Memoizado (e antes dos early-returns — Rules of Hooks): sem isso, cada
  // re-render recriava `linhas` (filter+map+sort de 400+ itens) e a nova
  // referência invalidava toda a cadeia de memos do PainelTabela.
  const linhas = useMemo(
    () =>
      dados
        // Ordem nova: entra depois da revisão; antiga: logo após o executivo.
        .filter(estaEmAprovacao)
        .map((row) => {
          const aprovacaoLimite = row[F.APROVACAO_LIMITE];

          return {
            cliente: row[F.NOME_DO_CLIENTE],
            rowPlanilha: row,
            ambiente: row[F.AMBIENTE],
            projetista: atribuicaoDe(row, atribuicoes)?.projetista || "",
            entregaContrato: row[F.ENTREGA_PREVISTA],
            aprovacaoLimite,
            valor: row[F.VALOR_DO_AMBIENTE],
            diasEmFila: fmtDiasEmFila(entradaNaAprovacao(row)),
            ...buildDeadlineStatus(aprovacaoLimite),
          };
        })
        .sort((left, right) => compareDatesAsc(left.aprovacaoLimite, right.aprovacaoLimite)),
    [dados, atribuicoes],
  );

  const totalizadores = useMemo(
    () => [
      { label: "Total de Ambientes", valor: linhas.length },
      {
        label: "Em Atraso",
        valor: linhas.filter((linha) => linha.statusVariant === "danger").length,
      },
    ],
    [linhas],
  );

  if (initialLoading) {
    return <PageLoader scope="aprovacao" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  return (
    <PainelTabela
      titulo="Aprovação de Executivo"
      exportPainelId="aprovacao-executivo"
      colunas={COLUNAS}
      linhas={linhas}
      totalizadores={totalizadores}
      densidade="compact"
      mostrarPaginacao={false}
      persistirNoBackend
    />
  );
}
